import { apiClient } from "./client";

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
}

/**
 * Get a presigned PUT URL for uploading an image to R2.
 */
export async function getPresignedUrl(
  folder: "posts" | "avatars" | "covers",
  file: File
) {
  return apiClient<PresignedUrlResponse>("/uploads/presigned-url", {
    method: "POST",
    body: {
      folder,
      contentType: file.type,
      fileName: file.name,
    },
  });
}

/**
 * Upload a file directly to R2 via presigned URL.
 * Returns the public URL and R2 key.
 */
export async function uploadImage(
  folder: "posts" | "avatars" | "covers",
  file: File,
  onProgress?: (percent: number) => void
): Promise<{ key: string; publicUrl: string }> {
  const { uploadUrl, key, publicUrl } = await getPresignedUrl(folder, file);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });

  return { key, publicUrl };
}

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
